import {
  ActionButton,
  ActionsGroup,
  Button,
  FAB,
  SearchPagination,
  Table,
  usePagedQuery,
  useShowNotification
} from '@elotech/components';
import { TableContainer, alert } from 'common/components';
import { useCrudRole, useLoading } from 'common/hooks';
import { copiarParaAreaDeTransferencia } from 'common/utils/ClipboardUtils';
import { FormEvent, useEffect, useState } from 'react';

import AnaliseInconsistenciaService, {
  FiltroRegras
} from '../../service/AnaliseInconsistenciaService';
import { AnaliseInconsistenciaRegra } from '../../type';
import { gerarScriptExclusaoRegra, gerarScriptRegra } from './gerarScriptRegra';
import { SeveridadeChip, severidadeOpcoes } from './SeveridadeChip';

type Props = {
  service: AnaliseInconsistenciaService;
  onNova: () => void;
  onEditar: (id: string) => void;
  onExcluida?: () => void;
};

type FiltroTela = {
  descricao: string;
  severidade: string;
  ativo: string;
};

const filtroVazio: FiltroTela = { descricao: '', severidade: '', ativo: '' };

const paraFiltroDoService = (filtro: FiltroTela): FiltroRegras => ({
  descricao: filtro.descricao.trim() || undefined,
  severidade: filtro.severidade || undefined,
  ativo: filtro.ativo === '' ? undefined : filtro.ativo === 'true'
});

export const RegrasInconsistenciaListComponent = ({
  service,
  onNova,
  onEditar,
  onExcluida
}: Props) => {
  const showNotification = useShowNotification();
  const permissions = useCrudRole<'analise_inconsistencia_read'>(
    'analise_inconsistencia'
  );
  const [isLoading, setLoading] = useLoading();
  const [filtroTela, setFiltroTela] = useState<FiltroTela>(filtroVazio);

  const { loading, values, pagination, doSearch, doPagedSearch } =
    usePagedQuery<AnaliseInconsistenciaRegra, FiltroRegras>({
      search: (filtros = {}, paginacao, sort) =>
        service.listarRegras(filtros, paginacao, sort),
      onError: () =>
        showNotification({
          level: 'error',
          message: 'Não foi possível carregar as regras.'
        })
    });

  useEffect(() => {
    doSearch(paraFiltroDoService(filtroVazio));
  }, []);

  const filtrar = (event: FormEvent) => {
    event.preventDefault();
    doSearch(paraFiltroDoService(filtroTela));
  };

  const alterarFiltro = (campo: keyof FiltroTela, valor: string) =>
    setFiltroTela(atual => ({ ...atual, [campo]: valor }));

  const copiarScript = (regra: AnaliseInconsistenciaRegra) =>
    copiarParaAreaDeTransferencia(gerarScriptRegra(regra))
      .then(() =>
        showNotification({
          level: 'success',
          message: 'Script da regra copiado para a área de transferência.'
        })
      )
      .catch(() =>
        showNotification({
          level: 'error',
          message: 'Não foi possível copiar o script da regra.'
        })
      );

  const copiarScriptExclusao = (id: string) =>
    copiarParaAreaDeTransferencia(gerarScriptExclusaoRegra(id))
      .then(() =>
        showNotification({
          level: 'success',
          message:
            'Regra excluída. Script de exclusão copiado para a área de transferência.'
        })
      )
      .catch(() =>
        showNotification({
          level: 'warning',
          message:
            'Regra excluída, mas não foi possível copiar o script de exclusão.'
        })
      );

  const onRemove = (id: string) =>
    alert({
      type: 'question',
      title: 'Excluir',
      text: 'Deseja mesmo excluir essa regra?',
      onConfirm: () => {
        setLoading(
          service
            .excluirRegra(id)
            .then(() => {
              doSearch();
              onExcluida?.();
              copiarScriptExclusao(id);
            })
            .catch(() =>
              showNotification({
                level: 'error',
                message: 'Não foi possível excluir a regra.'
              })
            )
        );
      }
    });

  return (
    <>
      <TableContainer hasElementsAbove>
        <form onSubmit={filtrar} className="row mb-xs">
          <div className="col-sm-5">
            <label htmlFor="filtro-descricao">Descrição</label>
            <input
              id="filtro-descricao"
              className="form-control"
              value={filtroTela.descricao}
              onChange={e => alterarFiltro('descricao', e.target.value)}
            />
          </div>
          <div className="col-sm-3">
            <label htmlFor="filtro-severidade">Severidade</label>
            <select
              id="filtro-severidade"
              className="form-control"
              value={filtroTela.severidade}
              onChange={e => alterarFiltro('severidade', e.target.value)}
            >
              <option value="">Todas</option>
              {severidadeOpcoes.map(opcao => (
                <option key={opcao.value} value={opcao.value}>
                  {opcao.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-sm-2">
            <label htmlFor="filtro-ativo">Ativo</label>
            <select
              id="filtro-ativo"
              className="form-control"
              value={filtroTela.ativo}
              onChange={e => alterarFiltro('ativo', e.target.value)}
            >
              <option value="">Todos</option>
              <option value="true">Sim</option>
              <option value="false">Não</option>
            </select>
          </div>
          <div className="col-sm-2">
            <label>&nbsp;</label>
            <div>
              <Button type="submit">Filtrar</Button>
            </div>
          </div>
        </form>
        <Table
          values={values}
          loading={loading || isLoading}
          keyExtractor={(regra: AnaliseInconsistenciaRegra) => `${regra.id}`}
        >
          <Table.Column
            header="Severidade"
            value={(regra: AnaliseInconsistenciaRegra) => (
              <SeveridadeChip severidade={regra.severidade} />
            )}
          />
          <Table.Column
            header="Descrição"
            value={(regra: AnaliseInconsistenciaRegra) => regra.descricao}
          />
          <Table.Column
            header="Ativo"
            value={(regra: AnaliseInconsistenciaRegra) =>
              regra.ativo ? 'Sim' : 'Não'
            }
          />
          <Table.Column
            header=""
            value={(regra: AnaliseInconsistenciaRegra, index: number) => (
              <ActionsGroup>
                <ActionButton
                  key="copy-script-button"
                  label="Copiar script"
                  icon="clipboard"
                  data-cy={`copy-script-button-${index}`}
                  onClick={() => copiarScript(regra)}
                />
                {permissions.update && (
                  <ActionButton
                    key="edit-button"
                    label="Editar"
                    icon="pencil-alt"
                    data-cy={`edit-button-${index}`}
                    onClick={() => onEditar(regra.id!)}
                  />
                )}
                {permissions.delete && (
                  <ActionButton
                    key="remove-button"
                    label="Remover"
                    icon="trash"
                    data-cy={`remove-button-${index}`}
                    onClick={() => onRemove(regra.id!)}
                  />
                )}
              </ActionsGroup>
            )}
          />
        </Table>
        {pagination && (
          <SearchPagination page={pagination} searchWithPage={doPagedSearch} />
        )}
      </TableContainer>
      {permissions.create && (
        <div className="btn-save">
          <FAB
            icon="plus"
            iconColor="white"
            title="Nova regra"
            onClick={onNova}
          />
        </div>
      )}
    </>
  );
};
